import NeptuneElement from '../../NeptuneElement.js';

class Chip extends NeptuneElement {

    static CHIP_TYPES = ['input', 'filter'];

    templateMarkup = `<button type="button"><slot></slot></button>`;
    componentCssUrl = '/src/components/core/chip/chip.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.validateRequiredProperties(['type']);
        this.processInitialAttributesAfterRender(Chip.observedAttributes);
        this.handleClick = this.handleClick.bind(this);
        this.setEventListeners();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['type', 'selected', 'disabled', 'dismissible', 'icon', 'show-dropdown', 'counter'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        const chipElement = this.shadowRoot.querySelector('button');
        if (!chipElement) {
            return;
        }

        switch (name) {
            case 'type': {
                this.validatePropertyValue(name, newValue, Chip.CHIP_TYPES);
                this.setChipContent();
                break;
            }
            case 'selected': {
                const isSelected = newValue === 'true';
                if (isSelected) {
                    chipElement.setAttribute('selected', '');
                } else {
                    chipElement.removeAttribute('selected');
                }
                break;
            }
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                if (isDisabled) {
                    chipElement.setAttribute('disabled', '');
                    chipElement.setAttribute('tabindex', '-1');
                } else {
                    chipElement.removeAttribute('disabled');
                    chipElement.setAttribute('tabindex', '0');
                }
                break;
            }
            case 'dismissible':
            case 'icon':
            case 'show-dropdown':
            case 'counter': {
                this.setChipContent();
                break;
            }
            default: {
                break;
            }
        }
    }

    handleClick(e) {
        if (this.hasAttribute('disabled')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
        }

        const type = this.getAttribute('type');
        const isDismissible = this.getAttribute('dismissible') === 'true';
        const showDropdown = this.getAttribute('show-dropdown') === 'true';
        const hasCounter = this.hasAttribute('counter');

        if (type === 'input') {
            if (isDismissible) {
                this.remove();
                return;
            }

            const isSelected = this.getAttribute('selected') === 'true';
            this.setAttribute('selected', String(!isSelected));
            return;
        }

        if (type === 'filter') {
            if (showDropdown || hasCounter) {
                const menuId = this.getAttribute('menu-id');
                let menu = null;

                if (menuId) {
                    menu = document.getElementById(menuId);
                } else {
                    menu = this.nextElementSibling;
                    if (menu && menu.tagName !== 'NEPTUNE-MENU') {
                        menu = null;
                    }
                }

                if (menu) {
                    menu.toggleAttribute('open');
                }
            }

            const isSelected = this.getAttribute('selected') === 'true';
            this.setAttribute('selected', String(!isSelected));
        }
    }

    setEventListeners() {
        const chipElement = this.shadowRoot.querySelector('button');
        if (chipElement) {
            chipElement.addEventListener('click', this.handleClick);
        }
    }

    removeEventListeners() {
        const chipElement = this.shadowRoot?.querySelector('button');
        if (chipElement) {
            chipElement.removeEventListener('click', this.handleClick);
        }
    }

    setChipContent() {
        const chipElement = this.shadowRoot.querySelector('button');
        if (!chipElement) {
            return;
        }

        const type = this.getAttribute('type');
        const icon = (this.getAttribute('icon') || '').trim();
        const isDismissible = this.getAttribute('dismissible') === 'true';
        const showDropdown = this.getAttribute('show-dropdown') === 'true';
        const counter = (this.getAttribute('counter') || '').trim();

        chipElement.innerHTML = '';

        const fragment = new DocumentFragment();

        if (icon) {
            const iconElement = document.createElement('i');
            iconElement.className = `fa-regular ${icon}`;
            fragment.appendChild(iconElement);
        }

        const slotElement = document.createElement('slot');
        fragment.appendChild(slotElement);

        if (type === 'input' && isDismissible) {
            const dismissIcon = document.createElement('i');
            dismissIcon.className = 'fa-solid fa-xmark';
            fragment.appendChild(dismissIcon);
        } else if (type === 'filter') {
            if (showDropdown) {
                const dropdownIcon = document.createElement('i');
                dropdownIcon.className = 'fa-solid fa-chevron-down';
                fragment.appendChild(dropdownIcon);
            } else if (counter) {
                const counterElement = document.createElement('span');
                counterElement.className = 'counter';
                counterElement.textContent = counter;
                fragment.appendChild(counterElement);
            }
        }

        chipElement.appendChild(fragment);
    }
}

customElements.define('neptune-chip', Chip);
export default Chip;